/**
 * "Unknown" is one value, however a file spells it (review #177, 2026-10-07).
 *
 * The pipeline fills a missing department, facility group, state or payer type with
 * "Unknown" in some places, "UNKNOWN" in others (an unresolved facility, by §7.2), and
 * leaves an empty string where the source had one rather than a null. The Overview
 * draws all of them as one "Unknown" bar, so a drill from that bar has to find every
 * spelling in the rows it lands on — matching the label exactly found none of the
 * blank ones, or none at all, and the drill fell back to "everyone who works it".
 */

/** The bar's and the dropdown's name for a blank value. */
export const UNKNOWN = 'Unknown'

const BLANK = /^\s*(unknown|none|null|n\/a|nan)?\s*$/i

/** Empty, whitespace, or a spelling of "unknown". */
export const isBlankValue = (v: string | null | undefined): boolean =>
  v === null || v === undefined || BLANK.test(v)

/** A value as the Overview names it: every blank spelling becomes "Unknown". */
export const valueName = (v: string | null | undefined): string =>
  isBlankValue(v) ? UNKNOWN : v!

/** The same value, counting every blank spelling as one. */
export const sameValue = (a: string | null | undefined, b: string | null | undefined): boolean =>
  a === b || (isBlankValue(a) && isBlankValue(b))

/** Every index in a dictionary that holds `v` (all the blank spellings, for a blank). */
export function indexesOf(dict: readonly string[], v: string): number[] {
  const out: number[] = []
  dict.forEach((d, i) => {
    if (sameValue(d, v)) out.push(i)
  })
  return out
}

/** The first index holding `v`, by `sameValue`; -1 when there is none. */
export const indexOfValue = (dict: readonly string[], v: string): number =>
  dict.findIndex((d) => sameValue(d, v))

/**
 * A dictionary's dropdown options, blank spellings folded into one "Unknown" (its value
 * the first of them), sorted by label. Picking it matches every spelling: see
 * `indexesOf`.
 */
export function dictOptions(dict: readonly string[]): { value: string; label: string }[] {
  const seen = new Set<string>()
  const out: { value: string; label: string }[] = []
  dict.forEach((d, i) => {
    const label = valueName(d)
    if (seen.has(label)) return
    seen.add(label)
    out.push({ value: String(i), label })
  })
  return out.sort((x, y) => x.label.localeCompare(y.label))
}
